// Notifications bell — header dropdown with unread count + live polling
import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Bell, Check } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatRelative = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "agora mesmo";
  if (mins < 60) return `há ${mins}min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `há ${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `há ${days}d`;
};

export const NotificationsBell = () => {
  const { user, getAuthHeaders } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const dropdownRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/notifications?limit=15`, { headers: getAuthHeaders() });
      setItems(res.data?.items || []);
      setUnread(res.data?.unread || 0);
    } catch { /* silently ignore */ }
  }, [user, getAuthHeaders]);

  // Initial fetch + polling every 30s
  useEffect(() => {
    if (!user) return;
    // eslint-disable-next-line
    fetchNotifications();
    const id = setInterval(fetchNotifications, 30000);
    return () => clearInterval(id);
  }, [user, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/mark-read`, {}, { headers: getAuthHeaders() });
      setItems((prev) => prev.map((it) => it.id === id ? { ...it, read: true } : it));
      setUnread((u) => Math.max(0, u - 1));
    } catch { /* swallow */ }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`, {}, { headers: getAuthHeaders() });
      setItems((prev) => prev.map((it) => ({ ...it, read: true })));
      setUnread(0);
    } catch { /* swallow */ }
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen((o) => !o)}
        title="Notificações"
        data-testid="notifications-bell"
        className="relative"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notifications-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </Button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-[360px] bg-white border border-slate-200 rounded-lg shadow-xl z-50"
          data-testid="notifications-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-2 border-b">
            <span className="text-sm font-semibold text-[var(--brand-primary)]">Notificações</span>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                className="text-[11px] text-brand-600 hover:text-brand-700 underline"
                data-testid="mark-all-read-btn"
              >
                Marcar todas como lidas
              </button>
            )}
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                Sem notificações
              </div>
            ) : (
              items.map((n) => (
                <div
                  key={n.id}
                  className={`px-4 py-3 border-b last:border-b-0 hover:bg-slate-50 transition cursor-pointer ${!n.read ? "bg-blue-50/40" : ""}`}
                  onClick={() => !n.read && markRead(n.id)}
                  data-testid={`notification-${n.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground truncate">{n.title}</div>
                      {n.message && (
                        <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</div>
                      )}
                      <div className="text-[10px] text-slate-400 mt-1">{formatRelative(n.created_at)}</div>
                    </div>
                    {!n.read && (
                      <span className="mt-1 w-2 h-2 bg-blue-500 rounded-full shrink-0" title="Não lida" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="px-4 py-2 border-t bg-slate-50 text-[11px] text-muted-foreground flex items-center gap-1">
            <Check className="w-3 h-3" /> Atualiza a cada 30s
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsBell;
